import React, { useEffect, useState } from "react";
import { Trash2, Loader2, Filter, X } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../../lib/api";
import { STATUS_STYLES, ORDER_STATUSES, gbp } from "../../../lib/constants";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../../components/ui/alert-dialog";

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [deleteId, setDeleteId] = useState(null);

  const load = (s, e) => {
    setLoading(true);
    const params = {};
    if (s) params.start_date = s;
    if (e) params.end_date = e;
    adminApi.get("/admin/orders", { params }).then((r) => setOrders(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => load(), []);

  const changeStatus = async (id, status) => {
    await adminApi.put(`/admin/orders/${id}/status`, { status });
    setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, status } : o)));
    toast.success("Status updated");
  };

  const doDelete = async () => {
    await adminApi.delete(`/admin/orders/${deleteId}`);
    setOrders((prev) => prev.filter((o) => o.id !== deleteId));
    setDeleteId(null);
    toast.success("Order deleted");
  };

  const fmt = (d) => new Date(d).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Europe/London" });

  return (
    <div>
      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-5 flex flex-wrap items-end gap-3">
        <div>
          <label className="text-xs text-gray-500 block mb-1">Start date</label>
          <input data-testid="orders-start-date" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-10 px-3 rounded-md border border-gray-200 text-sm" />
        </div>
        <div>
          <label className="text-xs text-gray-500 block mb-1">End date</label>
          <input data-testid="orders-end-date" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="h-10 px-3 rounded-md border border-gray-200 text-sm" />
        </div>
        <button data-testid="orders-apply-filter" onClick={() => load(start, end)} className="h-10 px-4 rounded-md bg-[#228B22] hover:bg-[#1A4331] text-white text-sm font-semibold flex items-center gap-2 transition-colors"><Filter size={15} /> Apply</button>
        <button data-testid="orders-clear-filter" onClick={() => { setStart(""); setEnd(""); load(); }} className="h-10 px-4 rounded-md border border-gray-300 text-sm font-semibold text-gray-600 flex items-center gap-2 hover:bg-gray-50 transition-colors"><X size={15} /> Clear</button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#228B22]" size={28} /></div>
      ) : orders.length === 0 ? (
        <p data-testid="admin-no-orders" className="text-center text-gray-400 py-16">No orders found.</p>
      ) : (
        <div className="space-y-4">
          {orders.map((o) => (
            <div key={o.id} data-testid={`admin-order-${o.id}`} className="bg-white rounded-lg border border-gray-200 p-5">
              <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-[#0A1F13]">{o.delivery_name}</span>
                    <span className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full border ${STATUS_STYLES[o.status] || ""}`}>{o.status}</span>
                  </div>
                  <p className="text-sm text-gray-500 mt-0.5">{o.delivery_phone} · {fmt(o.created_at)}</p>
                  <p className="text-sm text-gray-500">{o.delivery_address}, {o.delivery_postcode} · <span className="font-medium">{o.delivery_zone}</span></p>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={o.status} onValueChange={(v) => changeStatus(o.id, v)}>
                    <SelectTrigger data-testid={`order-status-select-${o.id}`} className="h-9 w-40 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ORDER_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <button data-testid={`delete-order-${o.id}`} onClick={() => setDeleteId(o.id)} className="p-2 rounded-md hover:bg-red-50 text-red-500"><Trash2 size={16} /></button>
                </div>
              </div>
              <table className="w-full text-sm mb-3">
                <tbody className="divide-y divide-gray-50">
                  {o.items.map((it, idx) => (
                    <tr key={idx}>
                      <td className="py-1.5 text-gray-600">{it.product_name} × {it.quantity}</td>
                      <td className="py-1.5 text-right font-medium">{gbp(it.subtotal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm border-t border-gray-100 pt-3">
                <span className="text-gray-500">Subtotal: <b className="text-[#0A1F13]">{gbp(o.subtotal)}</b></span>
                <span className="text-gray-500">Fixed: <b className="text-[#0A1F13]">{gbp(o.fixed_charge)}</b></span>
                <span className="text-gray-500">Delivery: <b className="text-[#0A1F13]">{gbp(o.outside_dorchester_charge)}</b></span>
                <span className="text-gray-500 ml-auto">Total: <b className="text-[#228B22]">{gbp(o.total_amount)}</b></span>
              </div>
              {o.notes && <p className="text-sm text-gray-500 mt-2 italic">Note: {o.notes}</p>}
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete order?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes the order.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-order" onClick={doDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
